import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  listAccountingOrders,
  markOrdersAsInvoiced,
} from "../../api/accountingApi";
import "./AdminAccountingFatturazione.css";
import { useNavigate } from "react-router-dom";
import OrderDetailModal from "../../components/orders/OrderDetailModal";
import Profilo from "../Profilo";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const CONTAB_LABELS = {
  non_fatturabile: "Non fatturabile",
  da_fatturare: "Da fatturare",
  fatturato: "Fatturato",
};

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // dd/mm/yy
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function centsToEur(cents) {
  if (cents == null) return "—";
  const n = Number(cents);
  if (!Number.isFinite(n)) return "";
  return (n / 100).toFixed(2);
}

function centsToEuroString(cents) {
  if (cents == null) return "—";
  const n = Number(cents || 0);
  return (n / 100).toFixed(2).replace(".", ",");
}

function csvEscape(field) {
  if (field == null) return "";
  const s = String(field);
  if (/[;"\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatCurrency(v) {
  const n = Number(v);
  const isCents = Number.isInteger(n) && n >= 50;
  const eur = isCents ? n / 100 : n;
  return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDateLabel(dateIso) {
  if (!dateIso) return "—";
  return formatDate(dateIso);
}

const EMPTY_TOTALS = {
  articoli_cents: 0,
  promo_cents: 0,
  articoli_netti_cents: 0,
  ns_articoli_cents: 0,
  rosa_cents: 0,
  ns_rosa_cents: 0,
  flat_fee_cents: 0,
  ns_total_cents: 0,
};

function todayYMD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function ymdToDisplay(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function displayToYmd(display) {
  if (!display) return "";
  const clean = String(display).trim();
  const m = clean.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;

  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";

  // valida davvero la data
  const valid =
    d.getFullYear() === Number(yyyy) &&
    d.getMonth() + 1 === Number(mm) &&
    d.getDate() === Number(dd);

  return valid ? `${yyyy}-${mm}-${dd}` : "";
}

function normalizeDateTyping(value) {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function DateFilterInput({ label, value, onChange }) {
  const [draft, setDraft] = useState(ymdToDisplay(value));
  const nativeRef = useRef(null);

  useEffect(() => {
    setDraft(ymdToDisplay(value));
  }, [value]);

  const openPicker = () => {
    if (nativeRef.current?.showPicker) {
      nativeRef.current.showPicker();
    } else {
      nativeRef.current?.focus();
      nativeRef.current?.click();
    }
  };

  const handleTextChange = (e) => {
    const nextDraft = normalizeDateTyping(e.target.value);
    setDraft(nextDraft);

    const ymd = displayToYmd(nextDraft);
    onChange(ymd);
  };

  const handleBlur = () => {
    const ymd = displayToYmd(draft);
    if (ymd) {
      setDraft(ymdToDisplay(ymd));
      onChange(ymd);
    } else if (!draft.trim()) {
      onChange("");
      setDraft("");
    } else {
      setDraft(value ? ymdToDisplay(value) : "");
    }
  };

  return (
    <div className="acc2-filter-group">
      <label className="acc2-label">{label}</label>

      <div className="acc2-date-wrap">
        <input
          type="text"
          inputMode="numeric"
          className="acc2-input acc2-input--date-text"
          value={draft}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder="gg/mm/aaaa"
          aria-label={label}
        />

        <button
          type="button"
          className="acc2-date-btn"
          onClick={openPicker}
          aria-label={`Apri calendario ${label}`}
          title={`Apri calendario ${label}`}
        >
          📅
        </button>

        <input
          ref={nativeRef}
          type="date"
          className="acc2-native-date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

export default function AdminAccountingFatturazione() {
  const [filters, setFilters] = useState({
    order_from: "",
    order_to: "",
    invoice_from: "",
    invoice_to: "",
    fornitore_id: "",
    contab_status: "all",
  });

  const [groupBy, setGroupBy] = useState("none");

  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [savingInvoice, setSavingInvoice] = useState(false);

  const [invoiceMeta, setInvoiceMeta] = useState({
    number: "",
    date: todayYMD(),
  });

  const [sort, setSort] = useState({
    field: "created_date",
    dir: "desc",
  });

  const [appliedDateFilters, setAppliedDateFilters] = useState({
    order_from: "",
    order_to: "",
    invoice_from: "",
    invoice_to: "",
  });

  const hasAppliedDateFilters = hasAnyDateFilter(appliedDateFilters);

  // ===== MODALE ORDINE (identica a AdminAccountingOrdini/AdminOrdini) =====
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // ===== MODALE CLIENTE (embed Profilo come AdminClienti) =====
  const [openClientId, setOpenClientId] = useState(null);

  function openOrderDetail(id) {
    setDetailId(id);
    setDetailOpen(true);
  }
  function closeOrderDetail() {
    setDetailOpen(false);
    setDetailId(null);
  }

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // ========== LOAD SUPPLIERS OPTIONS ==========
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingSuppliers(true);
        const res = await fetch(
          `${API_BASE}/api/admin/accounting/fornitori-options`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          },
        );
        const js = await res.json();
        if (!res.ok) throw new Error(js?.error || `HTTP ${res.status}`);
        if (!alive) return;
        setSuppliers(Array.isArray(js) ? js : []);
      } catch (err) {
        console.error("[Fatturazione] load suppliers error:", err);
        if (alive)
          toast.error(
            err?.message || "Errore caricamento elenco fornitori contabili",
          );
      } finally {
        if (alive) setLoadingSuppliers(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========== LOAD DATA ==========
  async function loadData(nextPage = 1, overrideFilters) {
    const effFilters = overrideFilters || filters;
    try {
      setLoading(true);
      setSelectedIds([]);
      const res = await listAccountingOrders({
        page: nextPage,
        pageSize,
        filters: effFilters,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotals(res.totals || EMPTY_TOTALS);
      setPage(res.page || nextPage);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error("[Fatturazione] loadData error:", err);
      toast.error(err?.message || "Errore caricamento ordini contabili");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========== SORTING ==========
  const sortedRows = useMemo(() => {
    if (!rows.length) return [];
    const { field, dir } = sort || {};
    if (!field) return [...rows];

    const factor = dir === "asc" ? 1 : -1;

    const getVal = (r) => {
      switch (field) {
        case "created_date":
          return r.created_date || "";
        case "id":
          return Number(r.id) || 0;
        case "cliente":
          return r.cliente || "";
        case "fornitore":
          return r.fornitore || "";
        case "citta":
          return r.citta || "";
        case "contab_status":
          return r.contab_status || "";
        case "articoli_cents":
          return Number(r.articoli_cents) || 0;
        case "promo_cents":
          return Number(r.promo_cents) || 0;
        case "articoli_netti_cents":
          return Number(r.articoli_netti_cents) || 0;
        case "ns_articoli_cents":
          return Number(r.ns_articoli_cents) || 0;
        case "rosa_cents":
          return Number(r.rosa_cents) || 0;
        case "ns_rosa_cents":
          return Number(r.ns_rosa_cents) || 0;
        case "flat_fee_cents":
          return Number(r.flat_fee_cents) || 0;
        case "ns_total_cents":
          return Number(r.ns_total_cents) || 0;
        case "invoice_number":
          return r.invoice_number || "";
        default:
          return r.created_date || "";
      }
    };

    const arr = [...rows];
    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);

      if (field === "created_date") {
        const ta = va ? new Date(va).getTime() : 0;
        const tb = vb ? new Date(vb).getTime() : 0;

        if (ta !== tb) {
          return dir === "asc" ? ta - tb : tb - ta;
        }

        const ida = Number(a.id) || 0;
        const idb = Number(b.id) || 0;
        return dir === "asc" ? ida - idb : idb - ida;
      }

      if (typeof va === "number" && typeof vb === "number") {
        if (va !== vb) {
          return dir === "asc" ? va - vb : vb - va;
        }

        const ida = Number(a.id) || 0;
        const idb = Number(b.id) || 0;
        return dir === "asc" ? ida - idb : idb - ida;
      }

      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();

      if (sa !== sb) {
        if (sa < sb) return dir === "asc" ? -1 : 1;
        return dir === "asc" ? 1 : -1;
      }

      const ida = Number(a.id) || 0;
      const idb = Number(b.id) || 0;
      return dir === "asc" ? ida - idb : idb - ida;
    });

    return arr;
  }, [rows, sort]);

  const handleSort = (field) => {
    setSort((prev) => {
      if (!prev || prev.field !== field) return { field, dir: "asc" };
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const renderSortIcon = (field) => {
    if (!sort || sort.field !== field)
      return <span className="acc2-sort-icon">↕</span>;
    return (
      <span className="acc2-sort-icon acc2-sort-icon--active">
        {sort.dir === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  // ========== GROUPING ==========
  const grouped = useMemo(() => {
    if (groupBy === "none") return null;

    const map = new Map();
    for (const r of sortedRows) {
      let key;
      let label;

      if (groupBy === "fornitore") {
        key = r.fornitore_id || r.fornitore || "n/a";
        label = r.fornitore || `Fornitore #${r.fornitore_id}`;
      } else if (groupBy === "citta") {
        key = r.citta || "n/a";
        label = r.citta || "Senza città";
      } else if (groupBy === "date") {
        key = r.created_date || "n/a";
        label = r.created_date
          ? formatDate(r.created_date)
          : "Data sconosciuta";
      } else if (groupBy === "invoice") {
        key = r.invoice_number || "senza_fattura";
        label = r.invoice_number || "Senza fattura";
      } else {
        key = "all";
        label = "Tutti";
      }

      const mapKey = String(key ?? "all");
      if (!map.has(mapKey)) {
        map.set(mapKey, {
          key: mapKey,
          label,
          rows: [],
          totals: { ...EMPTY_TOTALS },
        });
      }
      const g = map.get(mapKey);
      g.rows.push(r);
      g.totals.articoli_cents = g.totals.articoli_cents == null || r.articoli_cents == null ? null : g.totals.articoli_cents + Number(r.articoli_cents);
      g.totals.promo_cents = g.totals.promo_cents == null || r.promo_cents == null ? null : g.totals.promo_cents + Number(r.promo_cents);
      g.totals.articoli_netti_cents = g.totals.articoli_netti_cents == null || r.articoli_netti_cents == null ? null : g.totals.articoli_netti_cents + Number(r.articoli_netti_cents);
      g.totals.ns_articoli_cents += Number(r.ns_articoli_cents) || 0;
      g.totals.rosa_cents = g.totals.rosa_cents == null || r.rosa_cents == null ? null : g.totals.rosa_cents + Number(r.rosa_cents);
      g.totals.ns_rosa_cents += Number(r.ns_rosa_cents) || 0;
      g.totals.flat_fee_cents += Number(r.flat_fee_cents) || 0;
      g.totals.ns_total_cents += Number(r.ns_total_cents) || 0;
    }

    return Array.from(map.values());
  }, [sortedRows, groupBy]);

  // ========== FILTER HANDLERS ==========
  const handleDateChange = (field) => (val) => {
    setFilters((prev) => ({ ...prev, [field]: val }));
  };

  const handleClearFilters = () => {
    const cleared = {
      order_from: "",
      order_to: "",
      invoice_from: "",
      invoice_to: "",
      fornitore_id: "",
      contab_status: "all",
    };

    setFilters(cleared);
    setGroupBy("none");
    setSort({ field: "created_date", dir: "desc" });
    setInvoiceMeta((prev) => ({
      ...prev,
      number: "",
      date: todayYMD(),
    }));
    setAppliedDateFilters({
      order_from: "",
      order_to: "",
      invoice_from: "",
      invoice_to: "",
    });
    loadData(1, cleared);
  };

  const handleToggleDateFilters = () => {
    if (hasAppliedDateFilters) {
      const nextFilters = {
        ...filters,
        order_from: "",
        order_to: "",
        invoice_from: "",
        invoice_to: "",
      };

      setFilters(nextFilters);
      setAppliedDateFilters({
        order_from: "",
        order_to: "",
        invoice_from: "",
        invoice_to: "",
      });
      loadData(1, nextFilters);
      return;
    }

    const nextApplied = extractDateFilters(filters);
    setAppliedDateFilters(nextApplied);
    loadData(1, filters);
  };

  function extractDateFilters(src) {
    return {
      order_from: src.order_from || "",
      order_to: src.order_to || "",
      invoice_from: src.invoice_from || "",
      invoice_to: src.invoice_to || "",
    };
  }

  function hasAnyDateFilter(src) {
    return !!(
      src.order_from ||
      src.order_to ||
      src.invoice_from ||
      src.invoice_to
    );
  }

  function handleExportCsv() {
    if (!rows || rows.length === 0) {
      toast.error("Nessun dato da esportare");
      return;
    }

    const headers = [
      "ID ordine",
      "Data creazione",
      "Cliente",
      "Fornitore",
      "Città",
      "Provincia",
      "Stato contabile",
      "Tot. articoli",
      "Tot. promo",
      "Tot. articoli netti",
      "Nostro su articoli",
      "Tot. rosa",
      "Nostro su rosa",
      "Fisso",
      "Totale nostro",
    ];

    const lines = [];
    lines.push(headers.map(csvEscape).join(";"));

    rows.forEach((r) => {
      lines.push(
        [
          r.id,
          r.created_date
            ? new Date(r.created_date).toLocaleDateString("it-IT")
            : "",
          r.cliente || "",
          r.fornitore || "",
          r.citta || "",
          r.provincia || "",
          r.contab_status || "",
          centsToEuroString(r.articoli_cents),
          centsToEuroString(r.promo_cents),
          centsToEuroString(r.articoli_netti_cents),
          centsToEuroString(r.ns_articoli_cents),
          centsToEuroString(r.rosa_cents),
          centsToEuroString(r.ns_rosa_cents),
          centsToEuroString(r.flat_fee_cents),
          centsToEuroString(r.ns_total_cents),
        ]
          .map(csvEscape)
          .join(";"),
      );
    });

    if (totals) {
      lines.push("");
      lines.push(
        [
          "TOTALE",
          "",
          "",
          "",
          "",
          "",
          "",
          centsToEuroString(totals.articoli_cents),
          centsToEuroString(totals.promo_cents),
          centsToEuroString(totals.articoli_netti_cents),
          centsToEuroString(totals.ns_articoli_cents),
          centsToEuroString(totals.rosa_cents),
          centsToEuroString(totals.ns_rosa_cents),
          centsToEuroString(totals.flat_fee_cents),
          centsToEuroString(totals.ns_total_cents),
        ]
          .map(csvEscape)
          .join(";"),
      );
    }

    const csvContent = lines.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const y = today.getFullYear();
    const m = pad(today.getMonth() + 1);
    const d = pad(today.getDate());
    a.href = url;
    a.download = `proforma_${y}${m}${d}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const handleFornitoreChange = (e) => {
    const val = e.target.value;
    const nextFilters = { ...filters, fornitore_id: val };
    setFilters(nextFilters);
    loadData(1, nextFilters);
  };

  const handleContabStatusChange = (e) => {
    const val = e.target.value;
    const nextFilters = { ...filters, contab_status: val };
    setFilters(nextFilters);
    loadData(1, nextFilters);
  };

  const handleGroupByChange = (e) => setGroupBy(e.target.value);

  // Cliente: riuso della pagina AdminClients (Profilo embedded)
  function openClientDetail(id) {
    if (!id) return;
    setOpenClientId(id);
  }
  function closeClientDetail() {
    setOpenClientId(null);
  }

  // Fornitore: vai a gestione servizi fornitore
  function goSupplierServices(supplierId) {
    if (!supplierId) return;
    navigate(
      `/admin/gestioneservizifornitore?supplier=${encodeURIComponent(
        supplierId,
      )}&tab=0&q=`,
    );
  }

  // ========== SELECTION ==========
  const selectableIds = useMemo(
    () =>
      sortedRows
        .filter((r) => r.contab_status === "da_fatturare")
        .map((r) => r.id),
    [sortedRows],
  );

  const allSelectableSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelectableSelected) setSelectedIds([]);
    else setSelectedIds(selectableIds);
  };

  const toggleRow = (id, contabStatus) => {
    if (contabStatus !== "da_fatturare") return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // ========== MARK AS INVOICED ==========
  const handleMarkInvoiced = async () => {
    if (!selectedIds.length) {
      toast.error("Seleziona almeno un ordine da segnare come fatturato");
      return;
    }

    try {
      setSavingInvoice(true);
      const payloadInvoice =
        invoiceMeta.number || invoiceMeta.date ? invoiceMeta : undefined;
      const res = await markOrdersAsInvoiced(selectedIds, {
        invoice: payloadInvoice,
      });
      toast.success(
        `Segnati come fatturati ${res?.affected ?? selectedIds.length} ordini`,
      );
      setSelectedIds([]);
      loadData(page, filters);
    } catch (err) {
      console.error("[Fatturazione] markInvoiced error:", err);
      toast.error(
        err?.message || "Errore nel segnare gli ordini come fatturati",
      );
    } finally {
      setSavingInvoice(false);
    }
  };

  // ========== RENDER HELPERS ==========
  const renderContabStatus = (code) => {
    const label = CONTAB_LABELS[code] || code || "Sconosciuto";
    return (
      <span className={`acc2-status acc2-status--${code || "unknown"}`}>
        {label}
      </span>
    );
  };

  const renderRow = (r) => {
    const isSelectable = r.contab_status === "da_fatturare";
    const checked = selectedIds.includes(r.id);

    return (
      <tr key={r.id} className="acc2-row">
        <td className="acc2-cell acc2-cell--check">
          <input
            type="checkbox"
            checked={checked}
            disabled={!isSelectable}
            onChange={() => toggleRow(r.id, r.contab_status)}
          />
        </td>

        <td className="acc2-cell acc2-cell--date">
          <span className="acc2-cell-main">{formatDate(r.created_date)}</span>
        </td>

        {/* ORDINE: clic come AdminOrdini (openDetail) */}
        <td className="acc2-cell acc2-cell--id">
          <button
            type="button"
            className="acc2-link acc2-link--id"
            onClick={() => openOrderDetail(r.id)}
            title={`Apri ordine #${r.id}`}
          >
            <span className="acc2-eye" aria-hidden="true">
              👁️
            </span>
            <span className="acc2-mono">{r.id}</span>
          </button>
        </td>

        {/* FT: cliccabile (se vuoto non fa nulla) */}
        <td className="acc2-cell acc2-cell--ft">
          {r.invoice_number ? (
            <button
              type="button"
              className="acc2-link acc2-link--ft"
              onClick={() => {
                // minima spesa: per ora ti porto a filtro fatturazione/ordini con FT
                // Se vuoi un "dettaglio fattura" vero serve endpoint e/o pagina.
                toast(`FT ${r.invoice_number}`, { icon: "🧾" });
              }}
              title={`Fattura ${r.invoice_number}`}
            >
              {r.invoice_number}
            </button>
          ) : (
            <span className="acc2-cell-main">{""}</span>
          )}
        </td>

        {/* CLIENTE: clic -> modale cliente (Profilo embedded) */}
        <td className="acc2-cell acc2-cell--cliente">
          {r.cliente_id ? (
            <button
              type="button"
              className="acc2-link acc2-link--txt"
              onClick={() => openClientDetail(r.cliente_id)}
              title={`Apri cliente #${r.cliente_id}`}
            >
              <span className="acc2-cell-main">{r.cliente || "—"}</span>
            </button>
          ) : (
            <span className="acc2-cell-main">{r.cliente || "—"}</span>
          )}
        </td>

        {/* FORNITORE: clic -> gestione servizi fornitore */}
        <td className="acc2-cell acc2-cell--fornitore">
          {r.fornitore_id ? (
            <button
              type="button"
              className="acc2-link acc2-link--txt"
              onClick={() => goSupplierServices(r.fornitore_id)}
              title={`Vai a servizi fornitore #${r.fornitore_id}`}
            >
              <span className="acc2-cell-main">{r.fornitore || "—"}</span>
            </button>
          ) : (
            <span className="acc2-cell-main">{r.fornitore || "—"}</span>
          )}
        </td>

        <td className="acc2-cell acc2-cell--city">
          <span className="acc2-cell-main">
            {r.citta || "—"}
            {r.provincia ? ` (${String(r.provincia).toUpperCase()})` : ""}
          </span>
        </td>

        <td className="acc2-cell acc2-cell--status">
          {renderContabStatus(r.contab_status)}
        </td>

        <td className="acc2-cell acc2-cell--num">
          {centsToEur(r.articoli_cents)}
        </td>
        <td className="acc2-cell acc2-cell--num acc2-cell--promo">
          {Number(r.promo_cents) > 0 ? `−${centsToEur(r.promo_cents)}` : "—"}
        </td>
        <td className="acc2-cell acc2-cell--num">
          {centsToEur(r.articoli_netti_cents)}
        </td>
        <td className="acc2-cell acc2-cell--num acc2-cell--ns">
          {centsToEur(r.ns_articoli_cents)}
        </td>
        <td className="acc2-cell acc2-cell--num">{centsToEur(r.rosa_cents)}</td>
        <td className="acc2-cell acc2-cell--num acc2-cell--ns">
          {centsToEur(r.ns_rosa_cents)}
        </td>
        <td className="acc2-cell acc2-cell--num">
          {centsToEur(r.flat_fee_cents)}
        </td>
        <td className="acc2-cell acc2-cell--num acc2-cell--total">
          {centsToEur(r.ns_total_cents)}
        </td>
      </tr>
    );
  };

  return (
    <div className="acc2-page">
      <div className="acc2-inner">
        <header className="acc2-header">
          <div>
            <h1>Contabilità · Fatturazione</h1>
            {totals.articoli_cents == null && <p>«—» indica componenti storiche non archiviate: il relativo totale non viene ricostruito dai dati correnti. Le quote fatturate restano quelle originali.</p>}
            <p className="acc2-header-sub">
              Qui prepari la fatturazione: filtra gli ordini, controlla le
              nostre quote e registra ciò che è stato fatturato.
            </p>
          </div>
        </header>

        {/* FILTRI */}
        <section className="acc2-filters">
          <div className="acc2-filters-row">
            <DateFilterInput
              label="Da data ord"
              value={filters.order_from}
              onChange={handleDateChange("order_from")}
            />

            <DateFilterInput
              label="A data ord"
              value={filters.order_to}
              onChange={handleDateChange("order_to")}
            />

            <DateFilterInput
              label="Da data ft"
              value={filters.invoice_from}
              onChange={handleDateChange("invoice_from")}
            />

            <DateFilterInput
              label="A data ft"
              value={filters.invoice_to}
              onChange={handleDateChange("invoice_to")}
            />
            <div className="acc2-filter-group">
              <label className="acc2-label">Fornitore</label>
              <select
                className="acc2-input"
                value={filters.fornitore_id}
                onChange={handleFornitoreChange}
                disabled={loadingSuppliers}
              >
                <option value="">Tutti</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="acc2-filter-group">
              <label className="acc2-label">Stato contabile</label>
              <select
                className="acc2-input"
                value={filters.contab_status}
                onChange={handleContabStatusChange}
              >
                <option value="all">Tutti</option>
                <option value="non_fatturabile">Non fatturabile</option>
                <option value="da_fatturare">Da fatturare</option>
                <option value="fatturato">Fatturato</option>
              </select>
            </div>

            <div className="acc2-filter-group">
              <label className="acc2-label">Raggruppa per</label>
              <select
                className="acc2-input"
                value={groupBy}
                onChange={handleGroupByChange}
              >
                <option value="none">Nessuno</option>
                <option value="fornitore">Fornitore</option>
                <option value="citta">Città</option>
                <option value="date">Giorno</option>
                <option value="invoice">Fattura</option>
              </select>
            </div>
          </div>

          <div className="acc2-filters-row acc2-filters-row--bottom">
            <div className="acc2-filter-group acc2-filter-group--inline">
              <button
                type="button"
                className="acc2-btn acc2-btn--ghost"
                onClick={handleToggleDateFilters}
                disabled={loading}
              >
                {hasAppliedDateFilters ? "Pulisci date" : "Applica date"}
              </button>
            </div>

            <div className="acc2-filter-group acc2-filter-group--inline">
              <button
                type="button"
                className="acc2-btn acc2-btn--ghost"
                onClick={handleClearFilters}
                disabled={loading}
              >
                Pulisci filtri
              </button>
            </div>

            <div className="acc2-filter-group acc2-filter-group--inline">
              <button
                type="button"
                className="acc2-btn acc2-btn--ghost"
                onClick={handleExportCsv}
                disabled={loading || !rows.length}
              >
                Esporta CSV
              </button>
            </div>
          </div>
        </section>

        {/* TABELLA */}
        <section className="acc2-table-section">
          <div className="acc2-table-wrapper">
            <table className="acc2-table">
              <thead>
                <tr>
                  <th className="acc2-th acc2-th--check">
                    <input
                      type="checkbox"
                      onChange={toggleSelectAll}
                      checked={
                        allSelectableSelected && selectableIds.length > 0
                      }
                    />
                  </th>

                  <th
                    className="acc2-th acc2-th--date acc2-th--sortable"
                    onClick={() => handleSort("created_date")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Data</span>
                      {renderSortIcon("created_date")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--id acc2-th--sortable"
                    onClick={() => handleSort("id")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Ord.</span>
                      {renderSortIcon("id")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--ft acc2-th--sortable"
                    onClick={() => handleSort("invoice_number")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">FT</span>
                      {renderSortIcon("invoice_number")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--cliente acc2-th--sortable"
                    onClick={() => handleSort("cliente")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Cliente</span>
                      {renderSortIcon("cliente")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--fornitore acc2-th--sortable"
                    onClick={() => handleSort("fornitore")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Fornitore</span>
                      {renderSortIcon("fornitore")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--city acc2-th--sortable"
                    onClick={() => handleSort("citta")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Città</span>
                      {renderSortIcon("citta")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--status acc2-th--sortable"
                    onClick={() => handleSort("contab_status")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Contabilità</span>
                      {renderSortIcon("contab_status")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("articoli_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Tot articoli</span>
                      {renderSortIcon("articoli_cents")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("promo_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Promo</span>
                      {renderSortIcon("promo_cents")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("articoli_netti_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Art netti</span>
                      {renderSortIcon("articoli_netti_cents")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("ns_articoli_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">% articoli (ns)</span>
                      {renderSortIcon("ns_articoli_cents")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("rosa_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Tot rosa</span>
                      {renderSortIcon("rosa_cents")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("ns_rosa_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">% rosa (ns)</span>
                      {renderSortIcon("ns_rosa_cents")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("flat_fee_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Fisso</span>
                      {renderSortIcon("flat_fee_cents")}
                    </div>
                  </th>

                  <th
                    className="acc2-th acc2-th--num acc2-th--sortable"
                    onClick={() => handleSort("ns_total_cents")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Totale ns</span>
                      {renderSortIcon("ns_total_cents")}
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr className="acc2-row acc2-row--loading">
                    <td colSpan={16}>Caricamento ordini…</td>
                  </tr>
                )}

                {!loading && sortedRows.length === 0 && (
                  <tr className="acc2-row acc2-row--empty">
                    <td colSpan={16}>
                      Nessun ordine trovato con i filtri correnti.
                    </td>
                  </tr>
                )}

                {!loading &&
                  sortedRows.length > 0 &&
                  (groupBy === "none"
                    ? sortedRows.map((r) => renderRow(r))
                    : grouped?.map((g) => (
                        <React.Fragment key={g.key}>
                          <tr className="acc2-group-row">
                            <td colSpan={16}>
                              <div className="acc2-group-header">
                                <div className="acc2-group-title">
                                  {groupBy === "fornitore" && "Fornitore: "}
                                  {groupBy === "citta" && "Città: "}
                                  {groupBy === "date" && "Giorno: "}
                                  <strong>{g.label}</strong>
                                </div>
                                <div className="acc2-group-totals">
                                  <span>
                                    Tot. articoli cliente:{" "}
                                    <strong>
                                      {centsToEur(g.totals.articoli_cents)} €
                                    </strong>
                                  </span>
                                  <span>
                                    Tot. ns.:{" "}
                                    <strong>
                                      {centsToEur(g.totals.ns_total_cents)} €
                                    </strong>
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>

                          {g.rows.map((r) => renderRow(r))}

                          <tr className="acc2-group-total-row">
                            <td className="acc2-cell acc2-cell--check" />
                            <td
                              className="acc2-cell acc2-group-total-label"
                              colSpan={7}
                            >
                              Totale gruppo{" "}
                              {groupBy === "fornitore"
                                ? "fornitore"
                                : groupBy === "citta"
                                ? "città"
                                : groupBy === "invoice"
                                ? "fattura"
                                : "giorno"}
                              : <strong>{g.label}</strong>
                            </td>
                            <td className="acc2-cell acc2-cell--num">
                              {centsToEur(g.totals.articoli_cents)} €
                            </td>
                            <td className="acc2-cell acc2-cell--num acc2-cell--promo">
                              {Number(g.totals.promo_cents) > 0
                                ? `−${centsToEur(g.totals.promo_cents)} €`
                                : "—"}
                            </td>
                            <td className="acc2-cell acc2-cell--num">
                              {centsToEur(g.totals.articoli_netti_cents)} €
                            </td>
                            <td className="acc2-cell acc2-cell--num">
                              {centsToEur(g.totals.ns_articoli_cents)} €
                            </td>
                            <td className="acc2-cell acc2-cell--num">
                              {centsToEur(g.totals.rosa_cents)} €
                            </td>
                            <td className="acc2-cell acc2-cell--num">
                              {centsToEur(g.totals.ns_rosa_cents)} €
                            </td>
                            <td className="acc2-cell acc2-cell--num">
                              {centsToEur(g.totals.flat_fee_cents)} €
                            </td>
                            <td className="acc2-cell acc2-cell--num acc2-cell--total">
                              {centsToEur(g.totals.ns_total_cents)} €
                            </td>
                          </tr>
                        </React.Fragment>
                      )))}
              </tbody>
            </table>
          </div>

          <div className="acc2-totals-row">
            <div className="acc2-totals-panel">
              <div className="acc2-totals-line">
                <span className="acc2-totals-line-label">
                  Totali filtro corrente:
                </span>

                <span className="acc2-totals-chip">
                  <span className="acc2-totals-chip-label">
                    Articoli cliente
                  </span>
                  <span className="acc2-totals-chip-value">
                    {centsToEur(totals.articoli_cents)} €
                  </span>
                </span>

                <span className="acc2-totals-chip">
                  <span className="acc2-totals-chip-label">Promo</span>
                  <span className="acc2-totals-chip-value">
                    {Number(totals.promo_cents) > 0
                      ? `−${centsToEur(totals.promo_cents)} €`
                      : "—"}
                  </span>
                </span>

                <span className="acc2-totals-chip">
                  <span className="acc2-totals-chip-label">Articoli netti</span>
                  <span className="acc2-totals-chip-value">
                    {centsToEur(totals.articoli_netti_cents)} €
                  </span>
                </span>

                <span className="acc2-totals-chip">
                  <span className="acc2-totals-chip-label">Ns. articoli</span>
                  <span className="acc2-totals-chip-value">
                    {centsToEur(totals.ns_articoli_cents)} €
                  </span>
                </span>

                <span className="acc2-totals-chip">
                  <span className="acc2-totals-chip-label">Rosa cliente</span>
                  <span className="acc2-totals-chip-value">
                    {centsToEur(totals.rosa_cents)} €
                  </span>
                </span>

                <span className="acc2-totals-chip">
                  <span className="acc2-totals-chip-label">Ns. rosa</span>
                  <span className="acc2-totals-chip-value">
                    {centsToEur(totals.ns_rosa_cents)} €
                  </span>
                </span>

                <span className="acc2-totals-chip">
                  <span className="acc2-totals-chip-label">Fisso</span>
                  <span className="acc2-totals-chip-value">
                    {centsToEur(totals.flat_fee_cents)} €
                  </span>
                </span>

                <span className="acc2-totals-chip acc2-totals-chip--strong">
                  <span className="acc2-totals-chip-label">Totale ns.</span>
                  <span className="acc2-totals-chip-value acc2-totals-chip-value--strong">
                    {centsToEur(totals.ns_total_cents)} €
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="acc2-bottom-actions">
            <div className="acc2-invoice-controls">
              <div className="acc2-invoice-field">
                <label className="acc2-label acc2-label--inline">
                  N. fattura
                </label>
                <input
                  type="text"
                  className="acc2-input acc2-input--sm"
                  value={invoiceMeta.number}
                  onChange={(e) =>
                    setInvoiceMeta((prev) => ({
                      ...prev,
                      number: e.target.value,
                    }))
                  }
                  placeholder="Es. 2025/001"
                />
              </div>

              <div className="acc2-invoice-field">
                <label className="acc2-label acc2-label--inline">
                  Data fattura
                </label>
                <input
                  type="date"
                  className="acc2-input acc2-input--sm"
                  value={invoiceMeta.date}
                  onChange={(e) =>
                    setInvoiceMeta((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                />
              </div>

              <button
                type="button"
                className="acc2-btn acc2-btn--primary"
                onClick={handleMarkInvoiced}
                disabled={savingInvoice || !selectedIds.length}
              >
                {savingInvoice
                  ? "Registrazione..."
                  : `Segna fatturati (${selectedIds.length})`}
              </button>
            </div>

            <div className="acc2-pagination">
              <button
                type="button"
                className="acc2-btn acc2-btn--ghost"
                disabled={loading || page <= 1}
                onClick={() => loadData(page - 1)}
              >
                ← Pagina precedente
              </button>
              <span className="acc2-page-info">
                Pagina {page} di {totalPages}
              </span>
              <button
                type="button"
                className="acc2-btn acc2-btn--ghost"
                disabled={loading || page >= totalPages}
                onClick={() => loadData(page + 1)}
              >
                Pagina successiva →
              </button>
            </div>
          </div>
        </section>
      </div>

      {openClientId && (
        <div className="ac-overlay" role="dialog" aria-modal="true">
          <div className="ac-modal">
            <div className="ac-modal-h">
              <div className="ac-title">Cliente #{openClientId}</div>
              <button className="ac-close" onClick={closeClientDetail}>
                ✕
              </button>
            </div>

            <div className="ac-modal-body">
              <div className="ac-form">
                <div className="ac-profile-embed">
                  <Profilo embedded targetUserId={openClientId} />
                </div>
              </div>
            </div>

            <div className="ac-modal-f">
              <button className="ac-btn" onClick={closeClientDetail}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
      <OrderDetailModal
        open={detailOpen}
        orderId={detailId}
        onClose={closeOrderDetail}
      />
    </div>
  );
}
